import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PdfBulkDownloadService } from './pdf-bulk-download.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';

@Component({
    selector: 'gtw-pdf-bulk-download',
    templateUrl: './pdf-bulk-download.component.html',
    styleUrls: ['./pdf-bulk-download.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PdfBulkDownloadComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();
  /** CeHostDialog listens for this event (detail `'cancel'`) to close the MatDialog. */
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  // Data inputs
  rowData: any = {};
  GENERAL_CONFIG: any = {};
  userSettings: any = {};
  serverConfig: any = {};

  // Component state
  downloading = false;
  systemGeneratedRequest = false;
  downloadMessage = '';
  isState = false;
  currentTheme = 'theme-black';

  constructor(
    private pdfBulkDownloadService: PdfBulkDownloadService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.userSettings = header.userSettingsData || {};
    this.serverConfig = header.serverConfigData || {};
    this.currentTheme = this.userSettings?.user?.selected_theme || 'theme-black';
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? (rawRow[0] && typeof rawRow[0] === 'object' ? rawRow[0] : {}) : (rawRow || {});
    this.cdr.markForCheck();
  }

  init() {
    try {
      // Determine if this is a state request
      this.isState = window.location.href.includes('state-bulk-export-history');

      // Check if this is a system-generated request
      if (this.rowData["USER_NAME"] === "System" && !this.rowData["S3_URL"]) {
        this.downloadMessage = "System generated files are available in " + 
          (this.isState ? "TMEF_STATE_ENTITY_XML" : "TMEF_ENTITY_XML") +
          " table, please contact Tax IT Manager to download those files.";
        this.systemGeneratedRequest = true;
      }

      this.cdr.detectChanges();
    } catch (e) {
      console.error('Error in pdf-bulk-download init:', e);
    }
  }

  downloadFile() {
    if (!this.rowData.S3_URL) {
      this._snackBar.open('Something went wrong while downloading!', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.downloading = true;
    this.cdr.detectChanges();

    this.pdfBulkDownloadService.downloadS3(
      this.GENERAL_CONFIG.base_url,
      this.GENERAL_CONFIG.bulk_pdf_engine_api_url,
      this.rowData.S3_URL,
      this.userSettings?.user?.sso_id || ''
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.downloading = false;
        this.cdr.detectChanges();
        this.close();
      },
      (error: any) => {
        console.error('Download error:', error);
        this.downloading = false;
        this._snackBar.open('Error occurred while downloading. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  close(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
